import { useEffect, useRef } from "react";
import { PencilLine } from "lucide-react";
import type { PreviewResponse } from "../../types/preview";
import type { CheckoutBuyer, ReleasedDocument } from "../../types/api";
import { ConversionBlock } from "./ConversionBlock";
import type { Seal } from "./ConversionBlock";
import { DocumentSheet } from "./DocumentSheet";
import type { Fact } from "./DocumentSheet";
import { NotificadaSummary, hasNotificadaSummary } from "./NotificadaSummary";
import { PrintPortal, ReleaseOrientation, ReleasedHeader } from "./ReleasedPanel";

interface Props {
  facts: Fact[];
  buyer: CheckoutBuyer;
  conversion?: { title?: string; seals?: Seal[] };
  preview: PreviewResponse;
  released: ReleasedDocument | null;
  price: number;
  priceNote?: string;
  onEdit: () => void;
  onReleased: (doc: ReleasedDocument) => void;
  onRegenerate: () => void;
}

export function DocumentPreview({ facts, buyer, conversion, preview, released, price, priceNote, onEdit, onReleased, onRegenerate }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const isReleased = released !== null;
  useEffect(() => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    ref.current?.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "start" });
  }, [preview.protocolo, isReleased]);
  useEffect(() => {
    if (process.env.NODE_ENV !== "production") console.info(`Gepeto: prévia gerada (source=${preview.source})`);
  }, [preview.source]);

  return (
    <div ref={ref} data-testid="document-preview-wrapper" className="fade-up scroll-mt-24">
      <span data-testid="preview-source" data-source={preview.source} className="sr-only">{preview.source}</span>
      {released ? (
        <ReleasedHeader protocolo={released.protocolo} />
      ) : (
        <div className="mx-auto mb-4 flex w-full max-w-[794px] items-center justify-between gap-4">
          <p className="font-mono text-xs uppercase tracking-[0.16em] text-slate-500">Prévia do documento</p>
          <button type="button" onClick={onEdit} data-testid="preview-edit-button" className="inline-flex items-center gap-1.5 rounded-sm text-sm text-slate-400 underline-offset-4 transition-colors duration-200 hover:text-emerald-400 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500">
            <PencilLine aria-hidden="true" className="h-4 w-4" />Editar dados
          </button>
        </div>
      )}
      <DocumentSheet facts={facts} preview={preview} released={released} variant="screen" />
      {hasNotificadaSummary(preview, released) && <NotificadaSummary preview={preview} />}
      {released ? (
        <>
          <ReleaseOrientation preview={preview} />
          <PrintPortal>
            <DocumentSheet facts={facts} preview={preview} released={released} variant="print" />
          </PrintPortal>
        </>
      ) : (
        <ConversionBlock
          price={price}
          priceNote={priceNote}
          protocolo={preview.protocolo}
          buyer={buyer}
          title={conversion?.title}
          seals={conversion?.seals}
          onReleased={onReleased}
          onRegenerate={onRegenerate}
        />
      )}
    </div>
  );
}
